/**
 * Cleanup - public data cleanup qualifier page (/cleanup).
 *
 * v4.37 - 2026-10-29 - EXPO-QUALIFIER-01 live-test fix: a 422 from the server names the answer to fix (form.fixField).
 * v4.36 - 2026-10-29 - EXPO-QUALIFIER-01 (Branko 2026-10-29, SMB Expo): short description, "What we do" and
 *   "Questions" panels (each with Close), the questionnaire, then the offer. Option lists, prices, the referral
 *   discount and the offer end date come from GET /api/qualifier/form (server file cleanup_qualifier_offer.json);
 *   nothing here hardcodes them. The industry lists are the onboarding Census 2022 three levels. The server computes
 *   the outcome; the page only shows it. Link parameters: ?campaign=<code> (QR code) and ?ref=<referral code>.
 *   Mobile first. No account, no sign-in.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { portalRequestUngated } from '../services/portalRequest';

export interface Option { id: string; label: string; needs_text?: boolean; outcome?: string; tier?: string | null }
interface Group { code: string; title: string }
interface Subsector { code: string; title: string; industry_groups: Group[] }
interface Sector { code: string; title: string; subsectors: Subsector[] }
interface Offer {
  offer_ends_on: string;
  currency: string;
  tiers: Record<string, { price_cents: number; referral_discount_cents: number }>;
  size_options: Option[];
  system_options: Option[];
  problem_options: Option[];
  timing_options: Option[];
  too_big_choices: Option[];
}
export interface FormData {
  offer: Offer;
  offer_open: boolean;
  sectors: Sector[];
  campaign_valid: boolean;
  referral: { code: string; first_name: string; company_name: string } | null;
}
interface Result {
  outcome: string;
  tier: string | null;
  already_submitted: boolean;
  referral_applied?: boolean;
  offer_price_cents: number | null;
  discount_cents: number;
  price_shown_cents: number | null;
}

export const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);

const Price: React.FC<{ full: number; discount: number; currency: string }> = ({ full, discount, currency }) =>
  discount > 0 ? (
    <span>
      <Box component="s" sx={{ color: 'text.secondary', mr: 1 }}>{money(full, currency)}</Box>
      <Box component="strong">{money(full - discount, currency)}</Box>
    </span>
  ) : (
    <strong>{money(full, currency)}</strong>
  );

const Cleanup: React.FC = () => {
  const { t } = useTranslation('cleanup');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [params] = useSearchParams();
  const campaign = params.get('campaign') || '';
  const ref = params.get('ref') || '';

  const [data, setData] = useState<FormData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [panel, setPanel] = useState<'scope' | 'qa' | null>(null);

  const [v, setV] = useState({
    contact_name: '', company_name: '', email: '', phone: '', website: '', city: '', state: '',
    record_systems_other: '', main_problem_other: '', record_count_range: '', timing: '',
  });
  const [sector, setSector] = useState('');
  const [subsector, setSubsector] = useState('');
  const [group, setGroup] = useState('');
  const [systems, setSystems] = useState<string[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [askTooBig, setAskTooBig] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (campaign) qs.set('campaign', campaign);
    if (ref) qs.set('ref', ref);
    portalRequestUngated('/api/qualifier/form' + (qs.toString() ? `?${qs}` : ''))
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        setData(await res.json());
      })
      .catch(() => setLoadError(true));
  }, [campaign, ref]);

  const offer = data?.offer;
  const referral = data?.referral || null;
  const subsectors = useMemo(() => data?.sectors.find((s) => s.code === sector)?.subsectors || [], [data, sector]);
  const groups = useMemo(() => subsectors.find((s) => s.code === subsector)?.industry_groups || [], [subsectors, subsector]);
  const sizeOpt = offer?.size_options.find((o) => o.id === v.record_count_range);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setV({ ...v, [k]: e.target.value });
    setErrors({ ...errors, [k]: '' });
  };
  const toggle = (list: string[], setList: (x: string[]) => void, id: string, key: string) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
    setErrors({ ...errors, [key]: '' });
  };
  const needsText = (opts: Option[] = [], chosen: string[]) => opts.some((o) => o.needs_text && chosen.includes(o.id));

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    (['contact_name', 'company_name', 'city', 'state'] as const).forEach((k) => { if (!v[k].trim()) e[k] = t('form.required'); });
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email.trim())) e.email = t('form.invalidEmail');
    if (!sector) e.sector = t('form.required');
    if (!systems.length) e.systems = t('form.pickOne');
    if (needsText(offer?.system_options, systems) && !v.record_systems_other.trim()) e.record_systems_other = t('form.required');
    if (!v.record_count_range) e.size = t('form.required');
    if (!problems.length) e.problems = t('form.pickOne');
    if (needsText(offer?.problem_options, problems) && !v.main_problem_other.trim()) e.main_problem_other = t('form.required');
    if (!v.timing) e.timing = t('form.required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const send = async (tooBigChoice: string | null) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await portalRequestUngated('/api/qualifier/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...v,
          email: v.email.trim(),
          phone: v.phone.trim() || null,
          website: v.website.trim() || null,
          record_systems_other: v.record_systems_other.trim() || null,
          main_problem_other: v.main_problem_other.trim() || null,
          industry_code: group || subsector || sector,
          record_systems: systems,
          main_problems: problems,
          too_big_choice: tooBigChoice,
          campaign: campaign || null,
          ref: ref || null,
        }),
      });
      if (res.status === 429) { setSubmitError(t('form.tooMany')); return; }
      if (res.status === 422) {
        // v4.37 - EXPO-QUALIFIER-01 live-test fix: say which answer the server refused.
        const body = await res.json().catch(() => ({}));
        const field: string = body?.detail?.field || '';
        const label = t(`form.fieldLabels.${field}`, { defaultValue: '' });
        setSubmitError(label ? t('form.fixField', { field: label }) : t('form.error'));
        return;
      }
      if (!res.ok) { setSubmitError(t('form.error')); return; }
      setAskTooBig(false);
      setResult(await res.json());
    } catch {
      setSubmitError(t('form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (sizeOpt?.outcome === 'too_big') { setAskTooBig(true); return; }
    send(null);
  };

  if (loadError) {
    return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{t('form.loadError')}</Alert></Container>;
  }
  if (!data || !offer) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  const cur = offer.currency;
  const endDate = new Date(offer.offer_ends_on + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const tierLine = (tier: string, label: string) => {
    const tr = offer.tiers[tier];
    return (
      <Typography variant="h6" component="p" sx={{ fontWeight: 500 }}>
        {t(label, { price: '' })}
        <Price full={tr.price_cents} discount={referral ? tr.referral_discount_cents : 0} currency={cur} />
      </Typography>
    );
  };
  const referredLine = (tier?: string) => referral && (
    <Alert severity="success" sx={{ mt: 2 }}>
      {t('offer.referred', {
        name: referral.first_name,
        company: referral.company_name,
        amount: tier
          ? money(offer.tiers[tier].referral_discount_cents, cur)
          : Object.values(offer.tiers).map((x) => money(x.referral_discount_cents, cur)).join(' or '),
      })}
    </Alert>
  );

  const field = (k: keyof typeof v, label: string, extra: object = {}) => (
    <TextField fullWidth label={label} value={v[k]} onChange={set(k)} error={!!errors[k]} helperText={errors[k] || ' '} {...extra} />
  );

  const checkGroup = (opts: Option[], chosen: string[], setChosen: (x: string[]) => void, key: string) => (
    <FormGroup>
      {opts.map((o) => (
        <FormControlLabel key={o.id} control={<Checkbox checked={chosen.includes(o.id)} onChange={() => toggle(chosen, setChosen, o.id, key)} />} label={o.label} />
      ))}
    </FormGroup>
  );

  const qaItems = (t('qa.items', { returnObjects: true }) as { q: string; a: string }[]).map((x) => ({
    q: x.q,
    a: x.a.replace('{{small}}', money(offer.tiers.small?.price_cents ?? 0, cur)).replace('{{medium}}', money(offer.tiers.medium?.price_cents ?? 0, cur)),
  }));

  return (
    <Box sx={{ bgcolor: 'background.default', pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 3, md: 6 }, px: 2 }}>
        <Chip label={t('badge')} color="secondary" size="small" sx={{ mb: 2 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>{t('title')}</Typography>
        <Typography sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.125rem' } }}>{t('intro')}</Typography>
        {referredLine()}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
          <Button variant="contained" href="#qualify">{t('buttons.start')}</Button>
          <Button variant="outlined" onClick={() => setPanel('scope')}>{t('buttons.scope')}</Button>
          <Button variant="outlined" onClick={() => setPanel('qa')}>{t('buttons.qa')}</Button>
          <Button variant="text" component={RouterLink} to="/data-safety">{t('buttons.safety')}</Button>
        </Stack>

        <Paper id="qualify" component="form" noValidate onSubmit={onSubmit} variant="outlined" sx={{ mt: 4, p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>{t('form.title')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>{t('form.subtitle')}</Typography>
          {!data.offer_open && <Alert severity="info" sx={{ mb: 2 }}>{t('form.ended')}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
            {field('contact_name', t('form.name') + ' *', { autoComplete: 'name' })}
            {field('company_name', t('form.company') + ' *', { autoComplete: 'organization' })}
            {field('email', t('form.email') + ' *', { type: 'email', autoComplete: 'email', inputMode: 'email' })}
            {field('phone', t('form.phone'), { type: 'tel', autoComplete: 'tel' })}
            {field('website', t('form.website'), { autoComplete: 'url', inputMode: 'url' })}
            <Box />
            {field('city', t('form.city') + ' *', { autoComplete: 'address-level2' })}
            {field('state', t('form.state') + ' *', { autoComplete: 'address-level1' })}
          </Box>

          <FormLabel sx={{ display: 'block', mt: 1, mb: 1.5, color: 'text.primary', fontWeight: 600 }}>{t('form.industry')} *</FormLabel>
          <Stack spacing={2}>
            <TextField select fullWidth label={t('form.sector')} value={sector} error={!!errors.sector} helperText={errors.sector}
              onChange={(e) => { setSector(e.target.value); setSubsector(''); setGroup(''); setErrors({ ...errors, sector: '' }); }}>
              {data.sectors.map((s) => <MenuItem key={s.code} value={s.code}>{s.title}</MenuItem>)}
            </TextField>
            {subsectors.length > 0 && (
              <TextField select fullWidth label={t('form.subsector')} value={subsector} onChange={(e) => { setSubsector(e.target.value); setGroup(''); }}>
                {subsectors.map((s) => <MenuItem key={s.code} value={s.code}>{s.title}</MenuItem>)}
              </TextField>
            )}
            {groups.length > 0 && (
              <TextField select fullWidth label={t('form.group')} value={group} onChange={(e) => setGroup(e.target.value)}>
                {groups.map((g) => <MenuItem key={g.code} value={g.code}>{g.title}</MenuItem>)}
              </TextField>
            )}
          </Stack>

          <FormControl error={!!errors.systems} sx={{ mt: 3, display: 'block' }}>
            <FormLabel sx={{ color: 'text.primary', fontWeight: 600 }}>{t('form.systems')} *</FormLabel>
            {checkGroup(offer.system_options, systems, setSystems, 'systems')}
            {errors.systems && <FormHelperText>{errors.systems}</FormHelperText>}
          </FormControl>
          {needsText(offer.system_options, systems) && field('record_systems_other', t('form.systemsOther') + ' *')}

          <FormControl error={!!errors.size} sx={{ mt: 2, display: 'block' }}>
            <FormLabel sx={{ color: 'text.primary', fontWeight: 600 }}>{t('form.size')} *</FormLabel>
            <FormHelperText sx={{ mx: 0 }}>{t('form.sizeHelp')}</FormHelperText>
            <RadioGroup value={v.record_count_range} onChange={(e) => { setV({ ...v, record_count_range: e.target.value }); setErrors({ ...errors, size: '' }); }}>
              {offer.size_options.map((o) => <FormControlLabel key={o.id} value={o.id} control={<Radio />} label={o.label} />)}
            </RadioGroup>
            {errors.size && <FormHelperText>{errors.size}</FormHelperText>}
          </FormControl>

          <FormControl error={!!errors.problems} sx={{ mt: 2, display: 'block' }}>
            <FormLabel sx={{ color: 'text.primary', fontWeight: 600 }}>{t('form.problems')} *</FormLabel>
            {checkGroup(offer.problem_options, problems, setProblems, 'problems')}
            {errors.problems && <FormHelperText>{errors.problems}</FormHelperText>}
          </FormControl>
          {needsText(offer.problem_options, problems) && field('main_problem_other', t('form.problemsOther') + ' *', { multiline: true, minRows: 2 })}

          <FormControl error={!!errors.timing} sx={{ mt: 2, display: 'block' }}>
            <FormLabel sx={{ color: 'text.primary', fontWeight: 600 }}>{t('form.timing')} *</FormLabel>
            <RadioGroup value={v.timing} onChange={(e) => { setV({ ...v, timing: e.target.value }); setErrors({ ...errors, timing: '' }); }}>
              {offer.timing_options.map((o) => <FormControlLabel key={o.id} value={o.id} control={<Radio />} label={o.label} />)}
            </RadioGroup>
            <FormHelperText sx={{ mx: 0 }}>{errors.timing || t('form.timingHelp', { date: endDate })}</FormHelperText>
          </FormControl>

          {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
          <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting} sx={{ mt: 3, py: 1.5 }}>
            {submitting ? <CircularProgress size={24} color="inherit" /> : t('form.submit')}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>{t('form.privacy')}</Typography>
        </Paper>

        <Paper variant="outlined" sx={{ mt: 4, p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>{t('offer.title')}</Typography>
          {Object.keys(offer.tiers).map((tier) => <React.Fragment key={tier}>{tierLine(tier, `offer.${tier}`)}</React.Fragment>)}
          <Typography color="text.secondary">{t('offer.same')}</Typography>
          {referredLine()}
          <Typography color="text.secondary" sx={{ mt: 2 }}>{t('form.timingHelp', { date: endDate })}</Typography>
        </Paper>
      </Container>

      <Dialog open={panel !== null} onClose={() => setPanel(null)} fullScreen={fullScreen} maxWidth="sm" fullWidth>
        <DialogTitle>{panel === 'scope' ? t('scope.title') : t('qa.title')}</DialogTitle>
        <DialogContent dividers>
          {panel === 'scope' && (
            <>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {(t('scope.items', { returnObjects: true }) as string[]).map((s) => <li key={s}><Typography sx={{ mb: 1 }}>{s}</Typography></li>)}
              </Box>
              <Typography color="text.secondary" sx={{ mt: 2 }}>{t('scope.notIncluded')}</Typography>
            </>
          )}
          {panel === 'qa' && qaItems.map((x) => (
            <Box key={x.q} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>{x.q}</Typography>
              <Typography color="text.secondary">{x.a}</Typography>
            </Box>
          ))}
          {panel === 'qa' && <Link component={RouterLink} to="/data-safety">{t('buttons.safety')}</Link>}
        </DialogContent>
        <DialogActions><Button onClick={() => setPanel(null)}>{t('buttons.close')}</Button></DialogActions>
      </Dialog>

      <Dialog open={askTooBig} onClose={() => !submitting && setAskTooBig(false)} fullScreen={fullScreen} maxWidth="sm" fullWidth>
        <DialogTitle>{t('result.tooBigTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>{t('result.tooBigBody')}</Typography>
          <Stack spacing={1.5}>
            {offer.too_big_choices.map((c, i) => (
              <Button key={c.id} variant={i === 0 ? 'contained' : 'outlined'} disabled={submitting} onClick={() => send(c.id)}>{c.label}</Button>
            ))}
          </Stack>
          {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
        </DialogContent>
        <DialogActions><Button onClick={() => setAskTooBig(false)} disabled={submitting}>{t('buttons.close')}</Button></DialogActions>
      </Dialog>

      <Dialog open={result !== null} onClose={() => setResult(null)} fullScreen={fullScreen} maxWidth="sm" fullWidth>
        <DialogTitle>{result?.outcome === 'too_big' ? t('result.tooBigTitle') : t('result.qualifiedTitle')}</DialogTitle>
        <DialogContent dividers>
          {result?.already_submitted && <Typography>{t('result.already')}</Typography>}
          {!result?.already_submitted && result?.outcome === 'too_big' && <Typography>{t('result.tooBigSaved')}</Typography>}
          {!result?.already_submitted && result?.outcome === 'qualified_confirm_size' && <Typography>{t('result.confirmSizeBody')}</Typography>}
          {!result?.already_submitted && result?.outcome === 'qualified' && (
            <>
              <Typography>{t('result.qualifiedBody')}</Typography>
              {result.offer_price_cents != null && (
                <Typography variant="h6" component="p" sx={{ mt: 2 }}>
                  {t('result.price', { price: '' })}
                  <Price full={result.offer_price_cents} discount={result.discount_cents} currency={cur} />
                </Typography>
              )}
              {result.referral_applied && result.tier && referredLine(result.tier)}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setResult(null)}>{t('buttons.close')}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cleanup;
